import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'
import hash from 'object-hash'
import { StoreProfitDto } from '@merchx-v2/shared-types'
import axios from 'utils/axios'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'

const DEFAULT_PAGE_SIZE = 30

export type StoreProfitsPage = {
  items: StoreProfitDto[]
  total: number
  currentPage: number
  pageSize: number
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'storeProfitsPage', StoreProfitsPage>

const FETCH_STORE_PROFITS_LIST = `
  query fetchStoreProfitsPage ($page: Int, $size: Int, $storeId: Int!) {
    storeProfitsPage(page: $page, size: $size, storeId: $storeId) {
      items {
        id
        storeId
        event
        note
        payload
        sum
        createdAt
        updatedAt
      }
      total
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
  storeId: number
}

type FetchStoresProfitsListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchStoresPage = (args: FetchStoresProfitsListArgs) => Promise<StoreProfitsPage>

const fetchStoreProfitsPage: FetchStoresPage = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE, storeId } = pageParam || {}
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_STORE_PROFITS_LIST,
      variables: { page, size, storeId }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { storeProfitsPage } = data
    if (!storeProfitsPage) {
      throw new Error("Can't get store profits page!")
    }

    return {
      items: storeProfitsPage.items,
      total: storeProfitsPage.total,
      currentPage: page,
      pageSize: size
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

const queryOptions: UseInfiniteQueryOptions<StoreProfitsPage> = {
  keepPreviousData: true,
  refetchOnMount: true,
  retry: false,

  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0 }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0 }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize
      }
    }

    return undefined
  }
}

type Args = {
  storeId: number
}

export const useStoreProfitsList = (args: Args) => {
  const cacheKey = ['storeProfitsPage', hash(args)]

  return useInfiniteQuery(
    cacheKey,
    (prev) =>
      fetchStoreProfitsPage({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam, ...args }
      }),
    queryOptions
  )
}
